// Thin API client for the v2 Function App.
// In production the SWA reverse-proxies /api → Function App, so VITE_API_BASE
// defaults to '/api'. Set it explicitly during `func start` local dev.

const BASE = import.meta.env.VITE_API_BASE || '/api'

// Fetch with automatic retry on 503 (Consumption-plan Function App cold start).
async function fetchWithRetry(url, init) {
  let res = await fetch(url, init)
  if (res.status === 503) {
    await new Promise(r => setTimeout(r, 3000))
    res = await fetch(url, init)
  }
  return res
}

async function json(res) {
  if (res.status === 401) {
    // SWA will redirect via staticwebapp.config.json responseOverrides;
    // this is a belt-and-suspenders in case a call sneaks past.
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname)
    throw new Error('unauthenticated')
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`HTTP ${res.status}: ${body || res.statusText}`)
  }
  return res.json()
}

export async function fetchDeals({ signal } = {}) {
  const url = `${BASE}/deals?limit=10000`
  const res = await fetchWithRetry(url, { signal, credentials: 'include' })
  const body = await json(res)
  return body.deals || []
}

export async function fetchDims({ signal } = {}) {
  const url = `${BASE}/dims`
  const res = await fetchWithRetry(url, { signal, credentials: 'include' })
  return json(res)
}

export async function fetchMe() {
  // SWA endpoint that returns the current principal (or empty)
  try {
    const res = await fetch('/.auth/me', { credentials: 'include' })
    if (!res.ok) return null
    const data = await res.json()
    return data?.clientPrincipal || null
  } catch {
    return null
  }
}

async function jsonFetch(url, init = {}) {
  const res = await fetchWithRetry(url, { credentials: 'include', ...init })
  if (res.status === 401) {
    window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname)
    throw new Error('unauthenticated')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export async function fetchDealMarket(dealId, { signal } = {}) {
  const url = `${BASE}/deals/${encodeURIComponent(dealId)}/market`
  const res = await fetchWithRetry(url, { signal, credentials: 'include' })
  if (!res.ok) {
    if (res.status === 404) return null
    throw new Error(`HTTP ${res.status}`)
  }
  return res.json()
}

export async function submitDeal(payload) {
  return jsonFetch(`${BASE}/deals`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
  })
}

export async function requestLinkUpdate(dealId, links, notes) {
  return jsonFetch(`${BASE}/deals/${encodeURIComponent(dealId)}/request-link-update`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ ...links, notes }),
  })
}

export async function deleteDeal(dealId) {
  return jsonFetch(`${BASE}/deals/${encodeURIComponent(dealId)}`, {
    method: 'DELETE',
  })
}

export async function listIntake(status = 'pending_review') {
  return jsonFetch(`${BASE}/intake?status=${encodeURIComponent(status)}`)
}

export async function approveIntake(intakeId, overrides = {}) {
  return jsonFetch(`${BASE}/intake/${encodeURIComponent(intakeId)}/approve`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(overrides),
  })
}

export async function rejectIntake(intakeId, reason) {
  return jsonFetch(`${BASE}/intake/${encodeURIComponent(intakeId)}/reject`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ reason }),
  })
}
